import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { feedWhen, todoFromFeed } from '../../lib/todoCalendar';
import { swatch } from './MoreMenu';
import { Icon } from './icons';

/*
 * 077: the event calendars on the to-do Calendar - the picker that says
 * which are shown, and the card a feed event opens.
 *
 * THE PICKER. "Calendars" beside the filters: every calendar the firm
 * offers (Settings -> Calendars), grouped by category, a box each with its
 * colour. Malta's public holidays start on, the rest off; the choice is kept
 * on the server, so it follows the person to their phone. A tick is shown at
 * once and saved; if the save fails it goes back and says so.
 *
 * THE CARD. A feed event is somebody else's - read-only - so a click opens
 * a card rather than the to-do editor: the title, when, where, the start of
 * the description, "Open event page" (a new tab, rel noopener noreferrer)
 * and "Add to my to-dos", which makes a to-do of it on that day and time
 * with the link in its notes. Esc closes it and the focus goes back to the
 * event. On a phone it is a sheet along the bottom.
 */

/** Close on Escape and on a press outside `ref`. */
const useDismiss = (open, ref, onClose, ignore = null) => {
  useEffect(() => {
    if (!open) return undefined;
    const key = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };
    const down = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (ignore && ignore.current && ignore.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', down, true);
    return () => {
      document.removeEventListener('keydown', key, true);
      document.removeEventListener('pointerdown', down, true);
    };
  }, [open, ref, onClose, ignore]);
};

export function LayersPicker({ layers, onToggle, saving = false }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const button = useRef(null);
  const close = useRef(() => { setOpen(false); if (button.current) button.current.focus(); }).current;
  useDismiss(open, box, close, button);
  const on = layers.filter((l) => l.on).length;
  const groups = [];
  for (const l of layers) {
    const label = l.category_label || l.category || 'Other';
    let g = groups.find((x) => x.label === label);
    if (!g) { g = { label, items: [] }; groups.push(g); }
    g.items.push(l);
  }
  useEffect(() => {
    if (open && box.current) {
      const first = box.current.querySelector('input');
      if (first) first.focus();
    }
  }, [open]);
  return (
    <span className="tcal-layers">
      <button type="button" ref={button} className={`btn btn-secondary btn-small tcal-layers-btn${on ? ' is-on' : ''}`}
              aria-expanded={open} aria-haspopup="true" data-cal-layers-toggle=""
              onClick={() => setOpen((v) => !v)}>
        <Icon name="calendar" size={13} />
        <span>Calendars</span>
        {on > 0 && <span className="tcal-layers-count" aria-label={`${on} shown`}>{on}</span>}
      </button>
      {open && (
        <div className="tcal-layers-pop" ref={box} role="group" aria-label="Event calendars to show">
          <p className="tcal-layers-head">Show events from</p>
          {groups.map((g) => (
            <fieldset key={g.label} className="tcal-layers-group">
              <legend>{g.label}</legend>
              {g.items.map((l) => (
                <label key={l.id} className="tcal-layer" data-cal-layer={l.id}>
                  <input type="checkbox" checked={Boolean(l.on)} disabled={saving}
                         onChange={(e) => onToggle(l, e.target.checked)} />
                  <span className={`tcal-layer-dot ${swatch(l.color)}`} aria-hidden="true" />
                  <span className="tcal-layer-name">{l.name}</span>
                  {l.source && <span className="tcal-layer-src">{l.source}</span>}
                </label>
              ))}
            </fieldset>
          ))}
          <p className="tcal-layers-foot">Read-only events, kept up to date by Jarvis. Your choice follows you to every device.</p>
        </div>
      )}
    </span>
  );
}

/** The server only stores http(s) links; this is the second lock on the same door. */
const safeUrl = (u) => {
  try {
    const x = new URL(String(u || ''));
    return x.protocol === 'https:' || x.protocol === 'http:' ? x.href : null;
  } catch { return null; }
};

const EXCERPT = 320;
const excerpt = (s) => {
  const t = String(s || '').trim();
  if (t.length <= EXCERPT) return t;
  const cut = t.slice(0, EXCERPT - 1);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > EXCERPT * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s,.;:–-]+$/, '')}…`;
};

/** The card a feed event opens. `at` is the clicked element's rectangle; `sheet` puts it along the bottom. */
export function FeedPopover({ item, at, sheet = false, onClose, onAdd, busy = false }) {
  const box = useRef(null);
  const [pos, setPos] = useState(null);
  const [added, setAdded] = useState(false);
  const [problem, setProblem] = useState(null);
  useDismiss(true, box, onClose);

  useLayoutEffect(() => {
    if (sheet || !box.current || !at) { setPos(null); return; }
    const w = box.current.offsetWidth;
    const h = box.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = at.right + 8;
    if (left + w > vw - 12) left = at.left - w - 8;
    if (left < 12) left = Math.max(12, Math.min(vw - w - 12, at.left));
    let top = at.top;
    if (top + h > vh - 12) top = Math.max(12, vh - h - 12);
    setPos({ left, top });
  }, [at, sheet, item]);

  // Into the card once it is placed (a hidden element cannot take the focus).
  const placed = sheet || pos !== null;
  useEffect(() => {
    if (placed && box.current) {
      const f = box.current.querySelector('.tcal-pop-close');
      if (f) f.focus();
    }
  }, [item, placed]);

  const add = async () => {
    if (!onAdd || added) return;
    setProblem(null);
    try {
      await onAdd(todoFromFeed(item));
      setAdded(true);
    } catch {
      setProblem('Could not add it just now - try again.');
    }
  };

  const titleId = `tcal-pop-t-${String(item.id).replace(/[^a-z0-9-]/gi, '')}`;
  return (
    <>
      {sheet && <div className="tcal-pop-scrim" aria-hidden="true" />}
      <div ref={box} role="dialog" aria-modal={sheet ? 'true' : 'false'} aria-labelledby={titleId}
           className={`tcal-pop${sheet ? ' is-sheet' : ''}`} data-cal-pop={item.id}
           style={sheet ? undefined : pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}>
        <div className="tcal-pop-head">
          <span className={`tcal-layer-dot ${swatch(item.color)}`} aria-hidden="true" />
          <span className="tcal-pop-layer">{item.layer?.name}</span>
          <button type="button" className="tcal-pop-close" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={14} />
          </button>
        </div>
        <h4 className="tcal-pop-title" id={titleId}>{item.title}</h4>
        <p className="tcal-pop-line"><Icon name="clock" size={13} /><span>{feedWhen(item)}</span></p>
        {item.location && <p className="tcal-pop-line"><Icon name="home" size={13} /><span>{item.location}</span></p>}
        {item.description && <p className="tcal-pop-desc">{excerpt(item.description)}</p>}
        <div className="tcal-pop-actions">
          {safeUrl(item.url) && (
            <a className="btn btn-secondary btn-small" href={safeUrl(item.url)} target="_blank" rel="noopener noreferrer"
               data-cal-pop-link="">
              <Icon name="link" size={13} /> Open event page
            </a>
          )}
          {onAdd && (
            <button type="button" className="btn btn-primary btn-small" data-cal-pop-add="" disabled={busy || added}
                    onClick={add}>
              {added ? <><Icon name="check" size={13} /> Added to your to-dos</> : <><Icon name="plus" size={13} /> Add to my to-dos</>}
            </button>
          )}
        </div>
        {problem && <p className="tcal-pop-problem" role="alert">{problem}</p>}
        <p className="tcal-pop-note" role="status">
          {added ? 'It is in your list on that day, with the link in its notes.' : 'A public event - read-only here.'}
        </p>
      </div>
    </>
  );
}
